"use client";

import { useState, useEffect, useCallback } from 'react';
import Pagination from '../components/Pagination';
import {
    Plus,
    Edit,
    Trash2,
    Search,
    RefreshCw,
    X,
    Check,
    Filter,
    Download,
    Upload,
    ChevronLeft,
    ChevronRight,
    TrendingUp,
    Activity,
    Target,
    FileText,
    ArrowUpDown,
    ArrowUp,
    ArrowDown,
    Info,
    Printer,
    AlertCircle,
    AlertTriangle
} from 'lucide-react';
import * as XLSX from 'xlsx';
import {
    AdminGetAllDietCombinations,
    AdminCreateDietCombination,
    AdminUpdateDietCombination,
    AdminDeleteDietCombination,
    AdminBulkIngestDietCombinations,
    AdminExportDietCombinations,
    AdminSearchDietCombinations
} from '../components/Api';

export default function DietCombinationsPage() {
    const [combinations, setCombinations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [selectedCombination, setSelectedCombination] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [pageSize, setPageSize] = useState(6);
    const [showInstructions, setShowInstructions] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'diet', direction: 'asc' });
    const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });
    const [formErrors, setFormErrors] = useState({});
    const [formData, setFormData] = useState({
        sex: '',
        ageMin: '',
        ageMax: '',
        heightMin: '',
        heightMax: '',
        weightMin: '',
        weightMax: '',
        diet: '',
        fitnessGoal: '',
        calorieIntake: ''
    });

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    const fetchCombinations = useCallback(async (page = currentPage, size = pageSize, sort = sortConfig, search = searchTerm) => {
        setLoading(true);
        try {
            const response = search ? await AdminSearchDietCombinations(search, page, size) : await AdminGetAllDietCombinations(page, size);
            const data = response.data;
            if (data && data.content) {
                let sortedData = [...data.content];
                if (sort.key) {
                    sortedData.sort((a, b) => {
                        const aVal = a[sort.key] || '';
                        const bVal = b[sort.key] || '';
                        if (aVal < bVal) return sort.direction === 'asc' ? -1 : 1;
                        if (aVal > bVal) return sort.direction === 'asc' ? 1 : -1;
                        return 0;
                    });
                }
                setCombinations(sortedData);
                setTotalPages(data.totalPages);
                setTotalElements(data.totalElements);
                setCurrentPage(data.number);
            } else {
                setCombinations(data || []);
            }
        } catch (error) {
            console.error('Error fetching combinations:', error);
            showToast('Error fetching diet combinations', 'error');
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, sortConfig, searchTerm]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchCombinations();
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [fetchCombinations]);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const renderSortIcon = (key) => {
        if (sortConfig.key !== key) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
        return sortConfig.direction === 'asc' ?
            <ArrowUp className="w-3 h-3 ml-1 text-blue-600" /> :
            <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />;
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.sex) errors.sex = 'Sex is required';
        if (!formData.diet) errors.diet = 'Diet Type is required';
        if (!formData.ageMin || formData.ageMin.toString().trim() === '') errors.ageMin = 'Min Age is required';
        if (!formData.ageMax || formData.ageMax.toString().trim() === '') errors.ageMax = 'Max Age is required';
        if (!formData.heightMin || formData.heightMin.toString().trim() === '') errors.heightMin = 'Min Height is required';
        if (!formData.heightMax || formData.heightMax.toString().trim() === '') errors.heightMax = 'Max Height is required';
        if (!formData.weightMin || formData.weightMin.toString().trim() === '') errors.weightMin = 'Min Weight is required';
        if (!formData.weightMax || formData.weightMax.toString().trim() === '') errors.weightMax = 'Max Weight is required';
        if (!formData.fitnessGoal) errors.fitnessGoal = 'Fitness Goal is required';
        if (!formData.calorieIntake || formData.calorieIntake.toString().trim() === '') errors.calorieIntake = 'Calorie Intake is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCreate = async () => {
        if (!validateForm()) return;
        try {
            await AdminCreateDietCombination(formData);
            showToast('Diet combination created successfully');
            setShowCreateDialog(false);
            resetForm();
            fetchCombinations();
        } catch (error) {
            console.error('Error creating combination:', error);
            showToast('Error creating diet combination', 'error');
        }
    };

    const handleUpdate = async () => {
        if (!validateForm()) return;
        try {
            await AdminUpdateDietCombination(selectedCombination.id, formData);
            showToast('Diet combination updated successfully');
            setShowEditDialog(false);
            resetForm();
            fetchCombinations();
        } catch (error) {
            console.error('Error updating combination:', error);
            showToast('Error updating diet combination', 'error');
        }
    };

    const handleDelete = async (id) => {
        setConfirmModal({
            show: true,
            title: 'Delete Diet Combination',
            message: 'Are you sure you want to delete this diet combination? This action cannot be undone.',
            onConfirm: async () => {
                try {
                    await AdminDeleteDietCombination(id);
                    showToast('Diet combination deleted successfully');
                    fetchCombinations();
                } catch (error) {
                    console.error('Error deleting combination:', error);
                    showToast('Error deleting diet combination', 'error');
                } finally {
                    setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
                }
            }
        });
    };

    const handleDownloadTemplate = () => {
        // Create headers
        const headers = [
            'sex', 'ageMin', 'ageMax', 'heightMin', 'heightMax',
            'weightMin', 'weightMax', 'diet', 'fitnessGoal', 'calorieIntake'
        ];

        // Use Blob for CSV download
        // Explicitly use the correct format in the template examples
        const csvContent = headers.join(',') + '\n'
            + 'M,20,35,5.5,6.2,65,85,NON_VEGETARIAN,MUSCLE_BUILDING,2800\n'
            + 'F,18,30,5.0,5.8,45,60,VEGETARIAN,FAT_LOSS,1600';

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'diet_combination_template.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleBulkUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setLoading(true);
        try {
            // Pre-validation logic for CSV/Excel
            const reader = new FileReader();
            reader.onload = async (e) => {
                const data = e.target.result;
                const workbook = XLSX.read(data, { type: 'binary' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);

                // Validation rules
                const validDietTypes = ['VEGETARIAN', 'NON_VEGETARIAN'];
                const validFitnessGoals = ['FAT_LOSS', 'MUSCLE_BUILDING'];

                let errors = [];
                jsonData.forEach((row, index) => {
                    if (row.diet && !validDietTypes.includes(row.diet.toString().trim())) {
                        errors.push(`Row ${index + 2}: Invalid diet type "${row.diet}". Must be VEGETARIAN or NON_VEGETARIAN.`);
                    }
                    if (row.fitnessGoal && !validFitnessGoals.includes(row.fitnessGoal.toString().trim())) {
                        errors.push(`Row ${index + 2}: Invalid fitness goal "${row.fitnessGoal}". Must match enums (e.g., FAT_LOSS).`);
                    }
                });

                if (errors.length > 0) {
                    console.error('Validation errors:', errors);
                    showToast(`Validation failed: ${errors[0]}`, 'error');
                    setLoading(false);
                    return;
                }

                // If valid, proceed with upload
                try {
                    await AdminBulkIngestDietCombinations(file);
                    showToast('Bulk ingestion successful');
                    fetchCombinations();
                } catch (error) {
                    console.error('Error bulk uploading:', error);
                    showToast('Error in bulk ingestion', 'error');
                } finally {
                    setLoading(false);
                }
            };
            reader.readAsBinaryString(file);
        } catch (error) {
            console.error('Error reading file:', error);
            showToast('Error reading file', 'error');
            setLoading(false);
        }
    };

    const handleExport = async () => {
        try {
            const response = await AdminExportDietCombinations();
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'diet_combinations.xlsx');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            console.error('Error exporting diet combinations:', error);
            showToast('Error exporting diet combinations', 'error');
        }
    };

    const resetForm = () => {
        setFormErrors({});
        setFormData({
            sex: '',
            ageMin: '',
            ageMax: '',
            heightMin: '',
            heightMax: '',
            weightMin: '',
            weightMax: '',
            diet: '',
            fitnessGoal: '',
            calorieIntake: ''
        });
        setSelectedCombination(null);
    };

    const openEditDialog = (item) => {
        setSelectedCombination(item);
        setFormData({
            sex: item.sex,
            ageMin: item.ageMin,
            ageMax: item.ageMax,
            heightMin: item.heightMin,
            heightMax: item.heightMax,
            weightMin: item.weightMin,
            weightMax: item.weightMax,
            diet: item.diet,
            fitnessGoal: item.fitnessGoal,
            calorieIntake: item.calorieIntake
        });
        setShowEditDialog(true);
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {confirmModal.show && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4 backdrop-blur-sm transition-all duration-300">
                    <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl transform transition-all duration-300 scale-100 p-6 border border-gray-100">
                        <div className="flex flex-col items-center text-center">
                            <div className="bg-red-50 p-4 rounded-full mb-4">
                                <AlertTriangle className="w-8 h-8 text-red-500" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">
                                {confirmModal.title}
                            </h3>
                            <p className="text-gray-600 text-sm leading-relaxed mb-8">
                                {confirmModal.message}
                            </p>
                            <div className="flex gap-3 w-full">
                                <button
                                    onClick={() => setConfirmModal(prev => ({ ...prev, show: false }))}
                                    className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmModal.onConfirm}
                                    className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl shadow-lg shadow-red-200 transition-all duration-200 font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex justify-between items-center mb-6">
                <div />
                <div className="flex gap-2">
                    <button
                        onClick={() => setShowInstructions(true)}
                        className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                    >
                        <Info className="w-4 h-4" />
                        How it works?
                    </button>
                    <button
                        onClick={handleExport}
                        className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                    >
                        <Download className="w-4 h-4" />
                        Export Data
                    </button>
                    <label className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg cursor-pointer transition-colors text-sm font-medium">
                        <Upload className="w-4 h-4" />
                        Bulk Upload
                        <input type="file" className="hidden" accept=".csv, .xlsx, .xls" onChange={handleBulkUpload} />
                    </label>
                    <button
                        onClick={() => { resetForm(); setShowCreateDialog(true); }}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                    >
                        <Plus className="w-4 h-4" />
                        Add Combination
                    </button>
                </div>
            </div>

            <style>
                {`
                  @media print {
                    body * { visibility: hidden; }
                    .print-section, .print-section * { visibility: visible; }
                    .print-section { 
                      position: absolute; 
                      left: 0; 
                      top: 0; 
                      width: 100%; 
                      padding: 40px;
                      background: white;
                    }
                    .no-print { display: none !important; }
                  }
                `}
            </style>

            {/* Instructions Modal */}
            {showInstructions && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4 backdrop-blur-sm overflow-y-auto">
                    <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl p-6 border border-gray-100 max-h-[90vh] overflow-y-auto print-section">
                        <div className="flex justify-between items-center mb-6 no-print">
                            <div className="flex items-center gap-3">
                                <div className="bg-indigo-100 p-2 rounded-lg">
                                    <Info className="w-6 h-6 text-indigo-600" />
                                </div>
                                <h2 className="text-xl font-bold text-gray-900">Bulk Upload Instructions</h2>
                            </div>
                            <button
                                onClick={() => setShowInstructions(false)}
                                className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-lg transition-all"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="hidden print:block mb-8 text-center border-b pb-4">
                            <h1 className="text-3xl font-bold text-indigo-700">Diet Combination Bulk Upload Guide</h1>
                            <p className="text-gray-500 mt-2">Administrative Control Panel - BYB Cult</p>
                        </div>

                        <div className="space-y-6 text-sm text-gray-700">
                            <section className="bg-red-50 p-4 rounded-xl border border-red-100">
                                <h3 className="font-bold text-red-800 mb-2 flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4" />
                                    Strict Validation Warning
                                </h3>
                                <p className="text-red-700 font-medium">Diet Type and Fitness Goal MUST be in ALL CAPS with UNDERSCORES. Small letters or missing underscores will cause the upload to FAIL as the backend will not parse them.</p>
                                <ul className="list-disc list-inside mt-2 text-red-600 text-[11px]">
                                    <li>CORRECT: VEGETARIAN</li>
                                    <li>INCORRECT: vegetarian, Vegeterian, VEGETERANIAN</li>
                                    <li>CORRECT: NON_VEGETARIAN</li>
                                    <li>INCORRECT: non_vegetarian, Non Vegetarian, NON-VEGETARIAN</li>
                                </ul>
                            </section>

                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50 border-b border-gray-200">
                                            <th className="px-3 py-2 text-left font-bold text-gray-600">Column Name</th>
                                            <th className="px-3 py-2 text-left font-bold text-gray-600">Accepted Values / Description</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {[
                                            { col: 'sex', val: 'Male, Female, or Other' },
                                            { col: 'ageMin / ageMax', val: 'Numeric range (e.g., 20 - 30)' },
                                            { col: 'heightMin / heightMax', val: 'Numeric range in cm (e.g., 160 - 170)' },
                                            { col: 'weightMin / weightMax', val: 'Numeric range in kg (e.g., 60 - 70)' },
                                            { col: 'diet', val: 'STRICTLY: VEGETARIAN or NON_VEGETARIAN (must be all caps, no spaces, no lowercase)' },
                                            { col: 'fitnessGoal', val: 'FAT_LOSS, MUSCLE_BUILDING (must match exactly)' },
                                            { col: 'calorieIntake', val: 'Target calories (e.g., 2000)' },
                                        ].map((row, i) => (
                                            <tr key={i} className="border-b border-gray-50">
                                                <td className="px-3 py-3 font-semibold text-indigo-700 align-top w-1/3">{row.col}</td>
                                                <td className="px-3 py-3">{row.val}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="flex gap-4 no-print">
                                <button
                                    onClick={handleDownloadTemplate}
                                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-50 text-emerald-700 rounded-xl hover:bg-emerald-100 transition-colors font-bold border border-emerald-200"
                                >
                                    <Download className="w-5 h-5" />
                                    CSV Template
                                </button>
                                <button
                                    onClick={() => window.print()}
                                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all font-bold shadow-lg shadow-indigo-100"
                                >
                                    <Printer className="w-5 h-5" />
                                    Save as PDF / Print
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Grid Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                        <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 font-medium">Total Records</p>
                        <p className="text-xl font-bold text-gray-900">{totalElements}</p>
                    </div>
                </div>
            </div>

            {/* Table Container */}
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50/50">
                    <div className="relative w-72">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by diet, goal, sex, or calories..."
                            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm transition-all text-black placeholder-gray-400"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => fetchCombinations()}
                            className="p-2 hover:bg-white rounded-lg transition-colors border border-transparent hover:border-gray-200"
                        >
                            <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="p-12 text-center bg-white rounded-lg border border-gray-100">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="mt-4 text-gray-600 font-medium">Processing bulk upload...</p>
                            <p className="mt-1 text-sm text-gray-400">This may take a few moments depending on the file size.</p>
                        </div>
                    ) : (
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 text-gray-500 text-xs font-semibold uppercase tracking-wider">
                                <tr>
                                    <th className="px-6 py-4">S.No</th>
                                    <th className="px-6 py-4 cursor-pointer group" onClick={() => handleSort('sex')}>
                                        <div className="flex items-center">
                                            Sex {renderSortIcon('sex')}
                                        </div>
                                    </th>
                                    <th className="px-6 py-4 cursor-pointer group" onClick={() => handleSort('ageMin')}>
                                        <div className="flex items-center">
                                            Age Range {renderSortIcon('ageMin')}
                                        </div>
                                    </th>
                                    <th className="px-6 py-4 cursor-pointer group" onClick={() => handleSort('heightMin')}>
                                        <div className="flex items-center">
                                            Height (ft) {renderSortIcon('heightMin')}
                                        </div>
                                    </th>
                                    <th className="px-6 py-4 cursor-pointer group" onClick={() => handleSort('weightMin')}>
                                        <div className="flex items-center">
                                            Weight (kg) {renderSortIcon('weightMin')}
                                        </div>
                                    </th>
                                    <th className="px-6 py-4 cursor-pointer group" onClick={() => handleSort('diet')}>
                                        <div className="flex items-center">
                                            Diet {renderSortIcon('diet')}
                                        </div>
                                    </th>
                                    <th className="px-6 py-4 cursor-pointer group" onClick={() => handleSort('fitnessGoal')}>
                                        <div className="flex items-center">
                                            Goal {renderSortIcon('fitnessGoal')}
                                        </div>
                                    </th>
                                    <th className="px-6 py-4 cursor-pointer group" onClick={() => handleSort('calorieIntake')}>
                                        <div className="flex items-center">
                                            Calories (kcal) {renderSortIcon('calorieIntake')}
                                        </div>
                                    </th>
                                    <th className="px-6 py-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {combinations.map((item) => (
                                    <tr key={item.id} className="hover:bg-gray-50/80 transition-colors">
                                        <td className="px-6 py-4 text-sm text-gray-400 font-mono">
                                            {(currentPage * pageSize) + combinations.indexOf(item) + 1}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 rounded-md text-[10px] font-bold ${item.sex === 'M' ? 'bg-blue-100 text-blue-700' : item.sex === 'F' ? 'bg-pink-100 text-pink-700' : 'bg-gray-100 text-gray-700'}`}>
                                                {item.sex || '-'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600 font-medium">
                                            {item.ageMin != null && item.ageMax != null ? `${item.ageMin} - ${item.ageMax}` : '-'}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">
                                            {item.heightMin != null && item.heightMax != null ? `${item.heightMin} - ${item.heightMax}` : '-'}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">
                                            {item.weightMin != null && item.weightMax != null ? `${item.weightMin} - ${item.weightMax} kg` : '-'}
                                        </td>
                                        <td className="px-6 py-4">
                                            {item.diet ? (
                                                <span className="px-2 py-1 bg-green-50 text-green-700 rounded-full text-[10px] font-semibold border border-green-100">
                                                    {item.diet === 'NON_VEGETARIAN' ? 'Non-Vegetarian' : item.diet === 'VEGETARIAN' ? 'Vegetarian' : item.diet}
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600 font-medium">{item.fitnessGoal === 'FAT_LOSS' ? 'Fat Loss' : item.fitnessGoal === 'MUSCLE_BUILDING' ? 'Muscle Building' : item.fitnessGoal || '-'}</td>
                                        <td className="px-6 py-4 text-sm font-bold text-gray-900">{item.calorieIntake != null ? `${item.calorieIntake} kcal` : '-'}</td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={() => openEditDialog(item)}
                                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(item.id)}
                                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Pagination */}
            <Pagination
                currentPage={currentPage + 1}
                setCurrentPage={(page) => fetchCombinations(page - 1)}
                totalPages={totalPages}
                totalElements={totalElements}
                pageSize={pageSize}
                setPageSize={(size) => {
                    setPageSize(size);
                    fetchCombinations(0, size);
                }}
                itemName="Diet Combinations"
            />

            {/* Modal Overlay */}
            {(showCreateDialog || showEditDialog) && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                            <h2 className="text-xl font-bold text-gray-900">{showEditDialog ? 'Edit Combination' : 'Add New Combination'}</h2>
                            <button onClick={() => { setShowCreateDialog(false); setShowEditDialog(false); }} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>

                        <div className="p-6 grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Sex <span className="text-red-500">*</span></label>
                                <select
                                    className={`w-full px-4 py-2.5 rounded-xl border ${formErrors.sex ? 'border-red-500' : 'border-gray-200'} focus:ring-2 focus:ring-blue-500 outline-none text-sm transition-all text-black`}
                                    value={formData.sex}
                                    onChange={(e) => {
                                        setFormData({ ...formData, sex: e.target.value });
                                        if (formErrors.sex) setFormErrors({ ...formErrors, sex: '' });
                                    }}
                                >
                                    <option value="">Select Sex</option>
                                    <option value="M">Male</option>
                                    <option value="F">Female</option>
                                </select>
                                {formErrors.sex && <p className="text-red-500 text-xs mt-1">{formErrors.sex}</p>}
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Diet Type <span className="text-red-500">*</span></label>
                                <select
                                    className={`w-full px-4 py-2.5 rounded-xl border ${formErrors.diet ? 'border-red-500' : 'border-gray-200'} focus:ring-2 focus:ring-blue-500 outline-none text-sm transition-all text-black`}
                                    value={formData.diet}
                                    onChange={(e) => {
                                        setFormData({ ...formData, diet: e.target.value });
                                        if (formErrors.diet) setFormErrors({ ...formErrors, diet: '' });
                                    }}
                                >
                                    <option value="">Select Diet Type</option>
                                    <option value="VEGETARIAN">Vegetarian</option>
                                    <option value="NON_VEGETARIAN">Non-Vegetarian</option>
                                </select>
                                {formErrors.diet && <p className="text-red-500 text-xs mt-1">{formErrors.diet}</p>}
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Min Age <span className="text-red-500">*</span></label>
                                <input
                                    type="number"
                                    className={`w-full px-4 py-2.5 rounded-xl border ${formErrors.ageMin ? 'border-red-500' : 'border-gray-200'} focus:ring-2 focus:ring-blue-500 outline-none text-sm transition-all text-black placeholder-gray-400`}
                                    placeholder="e.g. 18"
                                    value={formData.ageMin}
                                    onChange={(e) => {
                                        setFormData({ ...formData, ageMin: e.target.value });
                                        if (formErrors.ageMin) setFormErrors({ ...formErrors, ageMin: '' });
                                    }}
                                />
                                {formErrors.ageMin && <p className="text-red-500 text-xs mt-1">{formErrors.ageMin}</p>}
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Max Age <span className="text-red-500">*</span></label>
                                <input
                                    type="number"
                                    className={`w-full px-4 py-2.5 rounded-xl border ${formErrors.ageMax ? 'border-red-500' : 'border-gray-200'} focus:ring-2 focus:ring-blue-500 outline-none text-sm transition-all text-black placeholder-gray-400`}
                                    placeholder="e.g. 30"
                                    value={formData.ageMax}
                                    onChange={(e) => {
                                        setFormData({ ...formData, ageMax: e.target.value });
                                        if (formErrors.ageMax) setFormErrors({ ...formErrors, ageMax: '' });
                                    }}
                                />
                                {formErrors.ageMax && <p className="text-red-500 text-xs mt-1">{formErrors.ageMax}</p>}
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Min Height (ft) <span className="text-red-500">*</span></label>
                                <input
                                    type="number"
                                    step="0.1"
                                    className={`w-full px-4 py-2.5 rounded-xl border ${formErrors.heightMin ? 'border-red-500' : 'border-gray-200'} focus:ring-2 focus:ring-blue-500 outline-none text-sm transition-all text-black placeholder-gray-400`}
                                    placeholder="e.g. 5.5"
                                    value={formData.heightMin}
                                    onChange={(e) => {
                                        setFormData({ ...formData, heightMin: e.target.value });
                                        if (formErrors.heightMin) setFormErrors({ ...formErrors, heightMin: '' });
                                    }}
                                />
                                {formErrors.heightMin && <p className="text-red-500 text-xs mt-1">{formErrors.heightMin}</p>}
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Max Height (ft) <span className="text-red-500">*</span></label>
                                <input
                                    type="number"
                                    step="0.1"
                                    className={`w-full px-4 py-2.5 rounded-xl border ${formErrors.heightMax ? 'border-red-500' : 'border-gray-200'} focus:ring-2 focus:ring-blue-500 outline-none text-sm transition-all text-black placeholder-gray-400`}
                                    placeholder="e.g. 6.2"
                                    value={formData.heightMax}
                                    onChange={(e) => {
                                        setFormData({ ...formData, heightMax: e.target.value });
                                        if (formErrors.heightMax) setFormErrors({ ...formErrors, heightMax: '' });
                                    }}
                                />
                                {formErrors.heightMax && <p className="text-red-500 text-xs mt-1">{formErrors.heightMax}</p>}
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Min Weight (kg) <span className="text-red-500">*</span></label>
                                <input
                                    type="number"
                                    className={`w-full px-4 py-2.5 rounded-xl border ${formErrors.weightMin ? 'border-red-500' : 'border-gray-200'} focus:ring-2 focus:ring-blue-500 outline-none text-sm transition-all text-black placeholder-gray-400`}
                                    placeholder="e.g. 50"
                                    value={formData.weightMin}
                                    onChange={(e) => {
                                        setFormData({ ...formData, weightMin: e.target.value });
                                        if (formErrors.weightMin) setFormErrors({ ...formErrors, weightMin: '' });
                                    }}
                                />
                                {formErrors.weightMin && <p className="text-red-500 text-xs mt-1">{formErrors.weightMin}</p>}
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Max Weight (kg) <span className="text-red-500">*</span></label>
                                <input
                                    type="number"
                                    className={`w-full px-4 py-2.5 rounded-xl border ${formErrors.weightMax ? 'border-red-500' : 'border-gray-200'} focus:ring-2 focus:ring-blue-500 outline-none text-sm transition-all text-black placeholder-gray-400`}
                                    placeholder="e.g. 80"
                                    value={formData.weightMax}
                                    onChange={(e) => {
                                        setFormData({ ...formData, weightMax: e.target.value });
                                        if (formErrors.weightMax) setFormErrors({ ...formErrors, weightMax: '' });
                                    }}
                                />
                                {formErrors.weightMax && <p className="text-red-500 text-xs mt-1">{formErrors.weightMax}</p>}
                            </div>

                            <div className="col-span-2 space-y-1">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Fitness Goal <span className="text-red-500">*</span></label>
                                <select
                                    className={`w-full px-4 py-2.5 rounded-xl border ${formErrors.fitnessGoal ? 'border-red-500' : 'border-gray-200'} focus:ring-2 focus:ring-blue-500 outline-none text-sm transition-all text-black`}
                                    value={formData.fitnessGoal}
                                    onChange={(e) => {
                                        setFormData({ ...formData, fitnessGoal: e.target.value });
                                        if (formErrors.fitnessGoal) setFormErrors({ ...formErrors, fitnessGoal: '' });
                                    }}
                                >
                                    <option value="">Select Fitness Goal</option>
                                    <option value="FAT_LOSS">Fat Loss</option>
                                    <option value="MUSCLE_BUILDING">Muscle Building</option>
                                </select>
                                {formErrors.fitnessGoal && <p className="text-red-500 text-xs mt-1">{formErrors.fitnessGoal}</p>}
                            </div>

                            <div className="col-span-2 space-y-1">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Calorie Intake Target (kcal) <span className="text-red-500">*</span></label>
                                <input
                                    type="number"
                                    className={`w-full px-4 py-2.5 rounded-xl border ${formErrors.calorieIntake ? 'border-red-500' : 'border-gray-200'} focus:ring-2 focus:ring-blue-500 outline-none text-sm transition-all text-black placeholder-gray-400`}
                                    placeholder="e.g. 2500"
                                    value={formData.calorieIntake}
                                    onChange={(e) => {
                                        setFormData({ ...formData, calorieIntake: e.target.value });
                                        if (formErrors.calorieIntake) setFormErrors({ ...formErrors, calorieIntake: '' });
                                    }}
                                />
                                {formErrors.calorieIntake && <p className="text-red-500 text-xs mt-1">{formErrors.calorieIntake}</p>}
                            </div>
                        </div>

                        <div className="p-6 bg-gray-50 flex justify-end gap-3 border-t border-gray-100">
                            <button
                                onClick={() => { setShowCreateDialog(false); setShowEditDialog(false); }}
                                className="px-6 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-white transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={showEditDialog ? handleUpdate : handleCreate}
                                className="px-8 py-2.5 rounded-xl bg-blue-600 text-white font-bold text-sm hover:bg-blue-700 shadow-lg shadow-blue-500/30 transition-all flex items-center gap-2"
                            >
                                <Check className="w-4 h-4" />
                                {showEditDialog ? 'Update Combination' : 'Create Combination'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast Notification */}
            {toast.show && (
                <div className={`fixed bottom-8 right-8 px-6 py-4 rounded-2xl shadow-2xl z-[100] flex items-center gap-3 animate-in fade-in slide-in-from-bottom-5 duration-300 ${toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                    }`}>
                    {toast.type === 'success' ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
                    <span className="font-bold">{toast.message}</span>
                </div>
            )}
        </div>
    );
}
